import { APP_CONFIG } from '@/constants/app';
import { AutomationExecution } from '@/features/domains/types/automation.types';
import type { DsnReturn } from '@/types/dsn-return.types';
import type { DsnDeclarationMonitor } from '@/types/dsn-declaration.types';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

// Automation service types
export interface AutomationConfiguration {
  id: string;
  uuid: string;
  folderUuid: string;
  domainUuid: string;
  templateId: string;
  isActive: boolean;
  dayOfMonth: number;
  notificationEmail: string;
  email?: string;
  sendToEmployeePortal: boolean;
  sendToManagerPortal: boolean;
  // New execution time fields
  executionHour: number;
  executionMinute: number;
  retryHour: number;
  retryMinute: number;
  // DSN specific fields
  generateBulletinOption?: 'generate-only' | 'generate-and-send';
  dsnOption?: 'calculate-only' | 'calculate-and-send';
  sendDsnByEmail?: boolean;
  dsnEmail?: string;
  customDescription?: string;
  // Edition specific fields
  editionOptionValue?: string;
  editionOptionName?: string;
  editionStartMonth?: number;
  editionStartYear?: number;
  editionEndMonth?: number;
  editionEndYear?: number;
  editionEmail?: string;
  usePreviousMonth?: boolean;
  /** Demande de variables : ce que le lien ouvre. */
  collecteCanal?: 'reponse' | 'saisie' | 'les_deux' | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  vaultSyncOptions?: {
    syncPayslips: boolean;
    syncDsn: boolean;
    syncToFolderVault: boolean;
    syncToEmployeeVaults: boolean;
    usePreviousMonth: boolean;
  };
}

/**
 * Ce qu'on envoie à l'API pour enregistrer une configuration.
 *
 * `uuid` et `createdBy` en sont volontairement absents : ils appartiennent au
 * serveur. Les avoir envoyés (avec `uuid: ''` pour une configuration neuve) a
 * rendu tout enregistrement d'automatisation impossible le 03/09/2026 —
 * Postgres refusant la chaine vide comme identifiant.
 */
export type AutomationConfigurationPayload = Omit<
  AutomationConfiguration,
  'id' | 'uuid' | 'createdAt' | 'updatedAt' | 'createdBy'
>;

export interface AutomationHistoryResponse {
  data: AutomationExecution[];
  total: number;
  hasMore: boolean;
}

export interface AutomationHistoryParams {
  limit?: number;
  offset?: number;
  status?: 'success' | 'failed' | 'running' | 'pending' | 'cancelled';
  templateId?: string;
}

// Create axios instance for automation service
const automationApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(automationApi);

export const automationService = {
  // Get automation configurations for a folder
  getFolderAutomations: async (
    folderUuid: string
  ): Promise<AutomationConfiguration[]> => {
    const response = await automationApi.get<{
      data: AutomationConfiguration[];
    }>(`/folders/${folderUuid}/automations`);
    return response.data.data;
  },

  // Save automation configurations for a folder
  saveFolderAutomations: async (
    folderUuid: string,
    configurations: AutomationConfigurationPayload[]
  ): Promise<AutomationConfiguration[]> => {
    const response = await automationApi.put<AutomationConfiguration[]>(
      `/folders/${folderUuid}/automations`,
      { configurations }
    );
    return response.data;
  },

  // Execute automation configuration immediately
  executeConfigurationNow: async (
    folderUuid: string,
    configurationUuid: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await automationApi.post<{
      data: {
        success: boolean;
        message: string;
      };
    }>(`/folders/${folderUuid}/automations/${configurationUuid}/execute`);
    return response.data.data;
  },

  // Get automation execution history for a folder
  getFolderAutomationHistory: async (
    folderUuid: string,
    params: AutomationHistoryParams = {}
  ): Promise<AutomationHistoryResponse> => {
    const queryParams = new URLSearchParams();

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.templateId) queryParams.append('templateId', params.templateId);

    const response = await automationApi.get<AutomationHistoryResponse>(
      `/folders/${folderUuid}/automations/history?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get details of a specific automation execution
  getAutomationExecution: async (
    executionUuid: string
  ): Promise<AutomationExecution> => {
    const response = await automationApi.get<AutomationExecution>(
      `/automations/executions/${executionUuid}`
    );
    return response.data;
  },

  // L'API enveloppe ses réponses (`{ data, meta }`, intercepteur global) :
  // c'est `data` qu'on rend, jamais l'enveloppe — sinon l'écran des pièces
  // en anomalie appelait `.flatMap` sur un objet et tombait dans
  // l'ErrorBoundary (vu sur le staging le 19/09/2026, défaut présent en
  // prod depuis 317294e).
  getDsnReturns: async (executionUuid: string): Promise<DsnReturn[]> => {
    const response = await automationApi.get<{ data: DsnReturn[] }>(
      `/automations/executions/${executionUuid}/dsn-returns`
    );
    return response.data.data;
  },

  getDsnDeclarationMonitor: async (
    folderUuid: string
  ): Promise<DsnDeclarationMonitor> => {
    const response = await automationApi.get<{
      data: DsnDeclarationMonitor;
    }>(`/folders/${folderUuid}/dsn-declarations`);
    return response.data.data;
  },

  refreshDsnDeclarationMonitor: async (
    folderUuid: string
  ): Promise<{ queued: true; completed: boolean; requestedAt: string }> => {
    const response = await automationApi.post<{
      data: {
        queued: true;
        completed: boolean;
        requestedAt: string;
      };
    }>(`/folders/${folderUuid}/dsn-declarations/refresh`);
    return response.data.data;
  },

  downloadDsnReturnArtifact: async (
    executionUuid: string,
    returnUuid: string,
    artifactUuid: string
  ): Promise<Blob> => {
    const response = await automationApi.get<Blob>(
      `/automations/executions/${executionUuid}/dsn-returns/${returnUuid}/artifacts/${artifactUuid}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Retry a failed automation execution
  retryAutomationExecution: async (
    executionUuid: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await automationApi.post<{
      success: boolean;
      message: string;
    }>(`/automations/executions/${executionUuid}/retry`);
    return response.data;
  },

  // Bulk update automation configurations for multiple folders
  bulkUpdateAutomations: async (
    folderUuids: string[],
    configurations: AutomationConfigurationPayload[]
  ): Promise<{
    configurations: Record<
      string,
      { uuid: string; templateId: string; isActive: boolean }[]
    >;
    message: string;
    foldersUpdated: number;
  }> => {
    const response = await automationApi.put<{
      configurations: Record<
        string,
        { uuid: string; templateId: string; isActive: boolean }[]
      >;
      message: string;
      foldersUpdated: number;
    }>('/automations/bulk-update', {
      folderUuids,
      configurations,
    });
    return response.data;
  },
};
